import { useState } from 'react';
import type { Resource } from '@/mocks/types';

interface MediaSectionProps {
  resource: Resource;
}

function VideoDemo({ resource }: MediaSectionProps) {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="rounded-lg border border-background-200 overflow-hidden bg-background-50">
      <div className="relative aspect-video bg-foreground-950">
        <img
          src={resource.cover}
          alt={`${resource.title} 播放器演示封面`}
          className={`w-full h-full object-cover object-top transition-opacity duration-500 ${playing ? 'opacity-40' : 'opacity-80'}`}
        />
        {!playing ? (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 cursor-pointer"
            aria-label="播放演示"
          >
            <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 hover:bg-primary-500 text-background-50 flex items-center justify-center transition-colors">
              <i className="ri-play-fill text-3xl md:text-4xl"></i>
            </span>
            <span className="px-3 py-1 rounded-full bg-foreground-950/60 text-background-100 text-xs">
              演示播放器 · 点击体验
            </span>
          </button>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-4">
            <span className="w-14 h-14 rounded-full bg-primary-500/90 text-background-50 flex items-center justify-center animate-pulse">
              <i className="ri-movie-2-line text-2xl"></i>
            </span>
            <p className="text-background-100 text-sm md:text-base font-medium">演示播放中…</p>
            <p className="text-background-400 text-xs md:text-sm">
              本区域为播放器演示，不提供真实视频播放
            </p>
            <button
              type="button"
              onClick={() => setPlaying(false)}
              className="mt-1 px-4 py-1.5 rounded-md bg-background-50/15 hover:bg-background-50/25 text-background-100 text-xs transition-colors cursor-pointer"
            >
              收起演示
            </button>
          </div>
        )}
      </div>

      <div className="p-3 md:p-4">
        <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
          <div className={`h-full bg-primary-500 rounded-full transition-all duration-1000 ${playing ? 'w-1/3' : 'w-0'}`}></div>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-3">
            <i className="ri-play-circle-line text-base"></i>
            <i className="ri-volume-up-line text-base"></i>
            <i className="ri-fullscreen-line text-base"></i>
          </span>
          <span>{resource.duration || '00:00'}</span>
        </div>
      </div>
    </div>
  );
}

function SoftwareShots({ resource }: MediaSectionProps) {
  const shots = resource.screenshots || [];
  const [active, setActive] = useState(0);

  const infoItems = [
    { label: '版本', value: resource.version, icon: 'ri-git-branch-line' },
    { label: '大小', value: resource.size, icon: 'ri-hard-drive-2-line' },
    { label: '平台', value: resource.platform, icon: 'ri-computer-line' },
  ].filter((item) => item.value);

  return (
    <div className="rounded-lg border border-background-200 overflow-hidden bg-background-50">
      <div className="relative aspect-video bg-background-100">
        {shots.length > 0 ? (
          <img
            src={shots[active]}
            alt={`${resource.title} 界面截图 ${active + 1}`}
            className="w-full h-full object-cover object-top"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-foreground-400">
            <i className="ri-image-2-line text-4xl"></i>
          </div>
        )}
        <span className="absolute bottom-3 left-3 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs">
          软件截图 {active + 1} / {shots.length}
        </span>
      </div>

      {shots.length > 1 && (
        <div className="flex gap-2 p-3 overflow-x-auto f91-scroll-hidden">
          {shots.map((shot, index) => (
            <button
              key={shot}
              type="button"
              onClick={() => setActive(index)}
              className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-colors cursor-pointer ${
                index === active ? 'border-primary-500' : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <img src={shot} alt={`${resource.title} 缩略图 ${index + 1}`} className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 p-3 md:p-4 border-t border-background-200">
        {infoItems.map((item) => (
          <div key={item.label} className="rounded-md bg-background-100 px-3 py-3">
            <span className="w-6 h-6 flex items-center justify-center text-primary-700 mb-1">
              <i className={item.icon}></i>
            </span>
            <p className="text-xs text-foreground-500">{item.label}</p>
            <p className="text-sm font-medium text-foreground-950 truncate">{item.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function TutorialChapters({ resource }: MediaSectionProps) {
  const chapters = resource.chapters || [];
  const [active, setActive] = useState(chapters[0]?.id || '');
  const [open, setOpen] = useState(false);
  const activeChapter = chapters.find((item) => item.id === active);

  return (
    <div className="rounded-lg border border-background-200 overflow-hidden bg-background-50">
      <div className="relative w-full aspect-[21/9] bg-background-200">
        <img
          src={resource.cover}
          alt={`${resource.title} 教程封面`}
          className="w-full h-full object-cover object-top"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-foreground-950/70 text-background-50 text-xs">
          教程封面
        </span>
      </div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="md:hidden w-full flex items-center justify-between px-4 py-3.5 text-sm font-medium text-foreground-900 cursor-pointer border-b border-background-200"
      >
        <span className="flex items-center gap-1.5">
          <i className="ri-list-unordered"></i>
          章节目录（共 {chapters.length} 节）
        </span>
        <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
      </button>

      <div className="md:grid md:grid-cols-5">
        <div className={`${open ? 'block' : 'hidden'} md:block md:col-span-2 md:border-r border-background-200 max-h-[420px] overflow-y-auto`}>
          <p className="hidden md:flex items-center gap-1.5 px-4 py-3 text-sm font-medium text-foreground-900 border-b border-background-200">
            <i className="ri-list-unordered"></i>
            章节目录（共 {chapters.length} 节）
          </p>
          {chapters.map((chapter, index) => (
            <button
              key={chapter.id}
              type="button"
              onClick={() => { setActive(chapter.id); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer border-b border-background-100 ${
                active === chapter.id ? 'bg-primary-50' : 'hover:bg-background-100'
              }`}
            >
              <span
                className={`shrink-0 w-6 h-6 rounded-md text-xs flex items-center justify-center ${
                  active === chapter.id
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200 text-foreground-600'
                }`}
              >
                {index + 1}
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm truncate ${active === chapter.id ? 'text-primary-800 font-medium' : 'text-foreground-800'}`}>
                  {chapter.title}
                </span>
              </span>
              <span className="shrink-0 text-xs text-foreground-400">{chapter.duration}</span>
            </button>
          ))}
        </div>

        <div className="md:col-span-3 p-5 md:p-6">
          {activeChapter && (
            <>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md bg-secondary-100 text-secondary-900 text-xs mb-3">
                <i className="ri-bookmark-line"></i>
                当前章节
              </span>
              <h3 className="font-heading font-semibold text-lg text-foreground-950">
                {activeChapter.title}
              </h3>
              <p className="mt-1 text-sm text-foreground-500">时长 {activeChapter.duration}</p>
              <div className="mt-5 aspect-video rounded-md bg-foreground-950 flex flex-col items-center justify-center gap-2 text-center px-4">
                <span className="w-12 h-12 rounded-full bg-primary-500/90 text-background-50 flex items-center justify-center">
                  <i className="ri-play-fill text-2xl"></i>
                </span>
                <p className="text-background-100 text-sm">章节内容演示区</p>
                <p className="text-background-400 text-xs">演示用途，不提供真实视频播放</p>
              </div>
              <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                本教程共 {chapters.length} 节，涵盖从基础到实战的完整路径。点击左侧目录可切换章节预览，
                每节均配有示例与练习，建议按顺序学习。
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MediaSection({ resource }: MediaSectionProps) {
  if (resource.category === 'video') return <VideoDemo resource={resource} />;
  if (resource.category === 'software') return <SoftwareShots resource={resource} />;
  return <TutorialChapters resource={resource} />;
}